import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import './GrantDocuments.css';
import { PROJECT_ROLES, normalizeProjectRole, isSamePerson } from '../utils/keyPersonnel';

// Same document IDs and personnel fields as the Subcontractor Documents Management
// page, so files uploaded here show up there (and vice versa).
const BIOSKETCH_DOC_ID = 5;

const CONSORTIUM_NOTE = 'The Subrecipient Statement of Intent and Scope of Work will be combined and submitted under "Consortium/Contractual Arrangements".';

const REQUIRED_DOCUMENTS = [
  { id: 1, name: 'Subrecipient Statement of Intent', description: CONSORTIUM_NOTE },
  { id: 4, name: 'Scope of Work', description: CONSORTIUM_NOTE },
  { id: 2, name: 'Sub-contract Budget' },
  { id: 3, name: 'Sub-contract Budget Justification' },
  { id: BIOSKETCH_DOC_ID, name: 'Biosketch of Key Personnel', isBiosketch: true, isMultiUpload: true },
  { id: 6, name: 'Facilities and Other Resources' },
  { id: 7, name: 'Major Equipment' },
  {
    id: 8,
    name: 'Letters of Support',
    description: 'Please combine all letters of support into a single pdf.',
    isOptional: true
  },
  {
    id: 9,
    name: 'Additional Documents',
    description: 'Anything else needed for this site. Add a note describing each file.',
    isOptional: true,
    isMultiUpload: true,
    hasNotes: true
  }
];

const EMPTY_BIOSKETCH = {
  firstName: '',
  middleName: '',
  lastName: '',
  positionTitle: '',
  organizationName: '',
  street1: '',
  street2: '',
  city: '',
  state: '',
  country: 'UNITED STATES',
  zipCode: '',
  phoneNumber: '',
  email: '',
  eraCommonsUsername: '',
  projectRole: ''
};

const REQUIRED_BIOSKETCH_FIELDS = [
  'firstName', 'lastName', 'positionTitle', 'organizationName',
  'street1', 'city', 'state', 'zipCode', 'country', 'phoneNumber', 'email'
];

const buttonStyle = (color, disabled) => ({
  padding: '8px 16px',
  backgroundColor: color,
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
  textDecoration: 'none'
});

const SubcontractUpload = () => {
  const { token } = useParams();
  const [tokenData, setTokenData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [additionalNotes, setAdditionalNotes] = useState('');

  // Biosketch personnel form, same fields as the management page.
  const [biosketchFormOpen, setBiosketchFormOpen] = useState(false);
  const [biosketchFormData, setBiosketchFormData] = useState(EMPTY_BIOSKETCH);
  const [biosketchFileName, setBiosketchFileName] = useState('');
  const [editingBiosketchFile, setEditingBiosketchFile] = useState(null);
  const [editingBiosketchOriginal, setEditingBiosketchOriginal] = useState(null);
  const biosketchFileInputRef = useRef(null);

  useEffect(() => {
    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validateToken = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/subcontract/validate/${token}`);

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Invalid or expired link');
      }

      const data = await response.json();
      setTokenData(data.tokenData);

      // Fetch existing uploaded files
      await fetchUploadedFiles();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Key personnel the questionnaire lists at this site; refreshed after each
  // biosketch save since the backend mirrors biosketches into the questionnaire.
  const refreshTokenData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/subcontract/validate/${token}`);
      if (response.ok) {
        const data = await response.json();
        setTokenData(data.tokenData);
      }
    } catch (err) {
      console.error('Error refreshing key personnel:', err);
    }
  };

  const fetchUploadedFiles = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/subcontract/files/${token}`);

      if (response.ok) {
        const data = await response.json();
        setUploadedFiles(data.files || []);
      }
    } catch (err) {
      console.error('Error fetching uploaded files:', err);
      // Don't show error to user, just log it
    }
  };

  const confirmUpload = async (body) => {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/subcontract/confirm-upload/${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error('File uploaded but its details could not be saved');
  };

  // Upload one file. Multi-upload documents get a timestamp so a second file
  // doesn't overwrite the first. Notes / biosketch details are attached afterwards
  // (custom headers on the signed PUT would fail the bucket's CORS preflight).
  const uploadFile = async (doc, file, { notes = '', biosketch = null } = {}) => {
    const storedFilename = doc.isMultiUpload
      ? `doc_${doc.id}_${Date.now()}_${file.name}`
      : `doc_${doc.id}_${file.name}`;

    const urlResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/subcontract/upload/${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: storedFilename, contentType: file.type })
    });
    if (!urlResponse.ok) throw new Error('Failed to get upload URL');
    const urlData = await urlResponse.json();

    const uploadResponse = await fetch(urlData.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file
    });
    if (!uploadResponse.ok) throw new Error('Failed to upload file');

    if (notes || biosketch) {
      await confirmUpload({ filename: storedFilename, notes, biosketch });
    }
  };

  const handleFileUpload = async (e, doc) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    try {
      setUploading(true);
      await uploadFile(doc, file, { notes: doc.hasNotes ? additionalNotes : '' });
      if (doc.hasNotes) setAdditionalNotes('');
      await fetchUploadedFiles();
    } catch (err) {
      alert(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (storedFilename) => {
    if (!window.confirm('Remove this file?')) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/subcontract/file/${token}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: storedFilename })
      });
      if (!response.ok) throw new Error('Failed to delete file');
      await fetchUploadedFiles();
    } catch (err) {
      alert(`Delete failed: ${err.message}`);
    }
  };

  // --- Biosketches ---
  const openBiosketchForm = (person = null) => {
    setEditingBiosketchFile(null);
    setEditingBiosketchOriginal(null);
    setBiosketchFormData({
      ...EMPTY_BIOSKETCH,
      organizationName: tokenData?.siteName || '',
      ...(person ? {
        firstName: person.firstName || '',
        lastName: person.lastName || '',
        positionTitle: person.positionTitle || '',
        eraCommonsUsername: person.eraCommonsUsername || '',
        projectRole: normalizeProjectRole(person.projectRole)
      } : {})
    });
    setBiosketchFileName('');
    if (biosketchFileInputRef.current) biosketchFileInputRef.current.value = '';
    setBiosketchFormOpen(true);
  };

  const openEditBiosketchForm = (file) => {
    const bio = file.biosketch || {};
    setEditingBiosketchFile(file.originalFilename);
    setEditingBiosketchOriginal({ firstName: bio.firstName || '', lastName: bio.lastName || '' });
    setBiosketchFormData({ ...EMPTY_BIOSKETCH, ...bio, projectRole: normalizeProjectRole(bio.projectRole) });
    setBiosketchFileName(file.filename || '');
    setBiosketchFormOpen(true);
  };

  const closeBiosketchForm = () => {
    setBiosketchFormOpen(false);
    setBiosketchFormData(EMPTY_BIOSKETCH);
    setBiosketchFileName('');
    setEditingBiosketchFile(null);
    setEditingBiosketchOriginal(null);
    if (biosketchFileInputRef.current) biosketchFileInputRef.current.value = '';
  };

  const handleBiosketchFormChange = (e) => {
    const { name, value } = e.target;
    setBiosketchFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBiosketchSubmit = async () => {
    const missing = REQUIRED_BIOSKETCH_FIELDS.filter(
      f => !biosketchFormData[f] || String(biosketchFormData[f]).trim() === ''
    );
    if (missing.length > 0) {
      alert('Please fill in all required fields before saving.');
      return;
    }

    try {
      setUploading(true);
      if (editingBiosketchFile) {
        // Editing only rewrites the details, not the file.
        await confirmUpload({
          filename: editingBiosketchFile,
          biosketch: biosketchFormData,
          match: editingBiosketchOriginal
        });
      } else {
        const file = biosketchFileInputRef.current?.files?.[0];
        if (!file) {
          alert('Please select a biosketch file to upload');
          return;
        }
        const extension = file.name.toLowerCase().split('.').pop();
        if (!['pdf', 'doc', 'docx'].includes(extension)) {
          alert('Please select a PDF or Word document for the biosketch.');
          return;
        }
        const personnelName = `${biosketchFormData.lastName}_${biosketchFormData.firstName}`.replace(/[^a-zA-Z0-9]/g, '_');
        const renamed = new File([file], `${personnelName}_${file.name}`, { type: file.type });
        await uploadFile({ id: BIOSKETCH_DOC_ID, isMultiUpload: true }, renamed, { biosketch: biosketchFormData });
      }
      await Promise.all([fetchUploadedFiles(), refreshTokenData()]);
      closeBiosketchForm();
    } catch (err) {
      alert(`Failed to save biosketch: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const renderBiosketchForm = () => {
    const field = (name, label, { required = true, type = 'text' } = {}) => (
      <div className={`form-group${required ? ' required' : ''}`}>
        <label htmlFor={`sub-bs-${name}`}>{label}:</label>
        <input
          type={type}
          id={`sub-bs-${name}`}
          name={name}
          value={biosketchFormData[name] || ''}
          onChange={handleBiosketchFormChange}
          required={required}
        />
      </div>
    );
    return (
      <div className="biosketch-upload-form" style={{ marginTop: '12px' }}>
        <div className="biosketch-header">
          <h5>{editingBiosketchFile ? 'Edit Personnel Biosketch' : 'Add Personnel Biosketch'}</h5>
        </div>
        <div className="form-row">
          {field('firstName', 'First Name')}
          {field('middleName', 'Middle Name', { required: false })}
          {field('lastName', 'Last Name')}
        </div>
        <div className="form-row">
          {field('positionTitle', 'Position/Title')}
          {field('organizationName', 'Organization Name')}
        </div>
        <div className="address-section">
          <h6>Business Address</h6>
          {field('street1', 'Street 1')}
          {field('street2', 'Street 2', { required: false })}
          <div className="form-row">
            {field('city', 'City')}
            {field('state', 'State')}
            {field('zipCode', 'Zip/Postal Code')}
            {field('country', 'Country')}
          </div>
        </div>
        <div className="form-row">
          {field('phoneNumber', 'Phone Number', { type: 'tel' })}
          {field('email', 'Email', { type: 'email' })}
        </div>
        <div className="form-row">
          {field('eraCommonsUsername', 'eRA Commons Username', { required: false })}
          <div className="form-group required">
            <label htmlFor="sub-bs-projectRole">Project Role:</label>
            <select
              id="sub-bs-projectRole"
              name="projectRole"
              value={biosketchFormData.projectRole}
              onChange={handleBiosketchFormChange}
              required
            >
              <option value="">-- Select --</option>
              {PROJECT_ROLES.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>
        </div>

        {editingBiosketchFile ? (
          <div className="form-group biosketch-file">
            <label>Biosketch File:</label>
            <span className="file-name">{biosketchFileName || 'Already uploaded'}</span>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280' }}>
              To swap the file itself, remove this biosketch and upload it again.
            </p>
          </div>
        ) : (
          <div className="form-group biosketch-file required">
            <label>Biosketch File:</label>
            <div className="file-upload-button">
              <button
                type="button"
                className="upload-file-btn"
                onClick={() => biosketchFileInputRef.current?.click()}
              >
                Select File
              </button>
              <span className="file-name">{biosketchFileName || 'No file selected'}</span>
            </div>
          </div>
        )}
        <input
          type="file"
          ref={biosketchFileInputRef}
          style={{ display: 'none' }}
          onChange={(e) => setBiosketchFileName(e.target.files?.[0]?.name || '')}
        />

        <div className="biosketch-form-actions">
          <button
            type="button"
            className="submit-btn"
            onClick={handleBiosketchSubmit}
            disabled={uploading || (!editingBiosketchFile && !biosketchFileName)}
          >
            {editingBiosketchFile
              ? (uploading ? 'Saving...' : 'Save Changes')
              : (uploading ? 'Uploading...' : 'Upload Biosketch')}
          </button>
          <button type="button" className="cancel-btn" onClick={closeBiosketchForm}>
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const renderBiosketchSection = (docFiles) => {
    const expected = tokenData?.keyPersonnel || [];
    const outstanding = expected.filter(person => !docFiles.some(f => isSamePerson(f.biosketch || {}, person)));
    return (
      <div style={{ marginTop: '12px' }}>
        <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#6b7280' }}>
          Enter the details for each key person at your organization and attach their biosketch.
          You can add key personnel who aren't listed yet.
        </p>

        {docFiles.length > 0 && (
          <div className="biosketches-list">
            {docFiles.map((f, i) => {
              const bio = f.biosketch || {};
              return (
                <div key={i} className="biosketch-item">
                  <div className="biosketch-info">
                    <span className="personnel-name">
                      {bio.firstName || bio.lastName
                        ? `${bio.firstName || ''} ${bio.middleName ? `${bio.middleName} ` : ''}${bio.lastName || ''}`.trim()
                        : f.filename}
                    </span>
                    {bio.positionTitle && <span className="position-title">{bio.positionTitle}</span>}
                    {bio.projectRole && <span className="project-role">Role: {bio.projectRole}</span>}
                    <span className="filename">{f.filename}</span>
                  </div>
                  <div className="biosketch-actions">
                    <button className="edit-btn small" onClick={() => openEditBiosketchForm(f)}>Edit</button>
                    {f.downloadUrl && (
                      <button className="view-btn small" onClick={() => window.open(f.downloadUrl, '_blank')}>View</button>
                    )}
                    <button className="remove-btn small" onClick={() => handleDeleteFile(f.originalFilename)}>Remove</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!biosketchFormOpen && outstanding.length > 0 && (
          <div className="biosketches-list" style={{ marginTop: '10px' }}>
            <h5>Biosketch still needed</h5>
            {outstanding.map((person, i) => (
              <div key={i} className="biosketch-item">
                <div className="biosketch-info">
                  <span className="personnel-name">{`${person.firstName || ''} ${person.lastName || ''}`.trim()}</span>
                  <span className="biosketch-meta">{person.projectRole || 'Listed in the application'}</span>
                </div>
                <div className="biosketch-actions">
                  <button className="edit-btn small" onClick={() => openBiosketchForm(person)}>Add Biosketch</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {biosketchFormOpen ? renderBiosketchForm() : (
          <button
            type="button"
            className="add-biosketch-btn"
            style={{ marginTop: '10px' }}
            onClick={() => openBiosketchForm()}
          >
            + Add Additional Key Personnel
          </button>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontFamily: 'Arial, sans-serif',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{
          maxWidth: '500px',
          padding: '40px',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          textAlign: 'center'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: '#fee2e2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
          </div>
          <h2 style={{ color: '#dc2626', marginBottom: '12px' }}>Invalid Link</h2>
          <p style={{ color: '#6b7280' }}>{error}</p>
          <p style={{ color: '#6b7280', fontSize: '14px', marginTop: '20px' }}>
            Please contact the grant administrator if you believe this is an error.
          </p>
        </div>
      </div>
    );
  }

  const requiredDocs = REQUIRED_DOCUMENTS.filter(d => !d.isOptional);
  const allRequiredUploaded = requiredDocs.every(d => uploadedFiles.some(f => f.docId === d.id));

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      padding: '40px 20px',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          marginBottom: '20px'
        }}>
          <h1 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '28px' }}>
            Sub-contract Document Upload
          </h1>
          <div style={{
            padding: '16px',
            backgroundColor: '#f0f9ff',
            borderLeft: '4px solid #3b82f6',
            borderRadius: '4px'
          }}>
            <p style={{ margin: '0 0 8px 0', color: '#1e40af', fontWeight: '600' }}>
              Contact: {tokenData?.contactName || 'N/A'}
            </p>
            <p style={{ margin: '0 0 8px 0', color: '#374151' }}>
              Site: {tokenData?.siteName || 'Performance Site'}
            </p>
            <p style={{ margin: '0', color: '#374151' }}>
              Company: {tokenData?.companyName || 'N/A'}
            </p>
          </div>
        </div>

        {/* Document Upload Section */}
        <div style={{
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{ margin: '0 0 20px 0', color: '#111827', fontSize: '20px' }}>
            Documents
          </h2>

          <div style={{ display: 'grid', gap: '16px' }}>
            {REQUIRED_DOCUMENTS.map(doc => {
              const docFiles = uploadedFiles.filter(f => f.docId === doc.id);
              const uploadedFile = docFiles[0];
              const isUploaded = docFiles.length > 0;
              return (
                <div key={doc.id} style={{
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  backgroundColor: isUploaded ? '#f0fdf4' : '#fafafa'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{
                        margin: '0 0 4px 0',
                        color: '#374151',
                        fontSize: '16px',
                        fontWeight: '600'
                      }}>
                        {doc.name}
                        {doc.isOptional && (
                          <span style={{ marginLeft: '8px', fontSize: '12px', fontWeight: 400, color: '#6b7280' }}>(Optional)</span>
                        )}
                      </h4>
                      {doc.description && (
                        <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#6b7280' }}>{doc.description}</p>
                      )}
                      {isUploaded && !doc.isMultiUpload && (
                        <p style={{ margin: 0, fontSize: '14px', color: '#059669' }}>
                          ✓ Uploaded: {uploadedFile.filename}
                        </p>
                      )}
                    </div>
                    {!doc.isMultiUpload && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {isUploaded && uploadedFile?.downloadUrl && (
                          <a
                            href={uploadedFile.downloadUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={buttonStyle('#6b7280', false)}
                          >
                            Download
                          </a>
                        )}
                        <input
                          type="file"
                          id={`file-${doc.id}`}
                          style={{ display: 'none' }}
                          onChange={(e) => handleFileUpload(e, doc)}
                          disabled={uploading}
                        />
                        <button
                          onClick={() => document.getElementById(`file-${doc.id}`).click()}
                          disabled={uploading}
                          style={buttonStyle(isUploaded ? '#10b981' : '#3b82f6', uploading)}
                        >
                          {uploading ? 'Uploading...' : (isUploaded ? 'Replace' : 'Upload')}
                        </button>
                      </div>
                    )}
                  </div>

                  {doc.isBiosketch && renderBiosketchSection(docFiles)}

                  {doc.hasNotes && (
                    <div style={{ marginTop: '12px' }}>
                      {docFiles.length > 0 && (
                        <div className="biosketches-list" style={{ marginBottom: '10px' }}>
                          {docFiles.map((f, i) => (
                            <div key={i} className="biosketch-item">
                              <div className="biosketch-info">
                                <span className="personnel-name">{f.notes || f.filename}</span>
                                {f.notes && <span className="biosketch-meta">{f.filename}</span>}
                              </div>
                              <div className="biosketch-actions">
                                {f.downloadUrl && (
                                  <button className="view-btn small" onClick={() => window.open(f.downloadUrl, '_blank')}>View</button>
                                )}
                                <button className="remove-btn small" onClick={() => handleDeleteFile(f.originalFilename)}>Remove</button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <label
                        htmlFor="sub-additional-notes"
                        style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}
                      >
                        Upload Name, Title, or Description:
                      </label>
                      <textarea
                        id="sub-additional-notes"
                        value={additionalNotes}
                        onChange={(e) => setAdditionalNotes(e.target.value)}
                        placeholder="Describe the document before adding the file"
                        rows={2}
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db', fontFamily: 'inherit', fontSize: '13px', boxSizing: 'border-box' }}
                      />
                      <input
                        type="file"
                        id={`file-${doc.id}`}
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileUpload(e, doc)}
                        disabled={uploading}
                      />
                      <button
                        onClick={() => document.getElementById(`file-${doc.id}`).click()}
                        disabled={uploading}
                        style={{ ...buttonStyle('#3b82f6', uploading), marginTop: '8px' }}
                      >
                        {uploading ? 'Uploading...' : '+ Upload (Optional)'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {allRequiredUploaded && (
            <div style={{
              marginTop: '24px',
              padding: '16px',
              backgroundColor: '#d1fae5',
              border: '1px solid #10b981',
              borderRadius: '6px',
              textAlign: 'center'
            }}>
              <p style={{ margin: 0, color: '#065f46', fontWeight: '600' }}>
                ✓ All required documents have been uploaded successfully!
              </p>
              <p style={{ margin: '8px 0 0 0', color: '#047857', fontSize: '14px' }}>
                You may close this window. Thank you for your submission.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          marginTop: '30px',
          textAlign: 'center',
          color: '#6b7280',
          fontSize: '14px'
        }}>
          <p>Powered by StreamLaunch</p>
          <p style={{ marginTop: '8px' }}>
            If you have questions, please contact the grant administrator
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubcontractUpload;
